/*
 * Copyright (c) 2023 Irian Software Development SRL. All Rights Reserved.
 * This software is the proprietary information of Irian Software Development SRL.
 * Use is subject to license and non-disclosure terms
 */

import {Injectable} from '@angular/core';
import {Observable, of, ReplaySubject} from 'rxjs';
import {NotificationVoModel} from '../models/notificationVo.model';
import {NotificationEndpoint} from './notification.endpoint';
import {map} from 'rxjs/operators';
import * as _ from 'lodash-es';

@Injectable()
export class NotificationDataService {

  private count: ReplaySubject<string> = new ReplaySubject();
  private notifications: NotificationVoModel[] = [];

  constructor(private notificationEndpoint: NotificationEndpoint) {
    this.count.next('0');

    this.getHotNotification();

  }

  public getNotification(): Observable<NotificationVoModel[]> {
    return of(this.notifications);
  }

  public getNotificationCount(): Observable<string> {
    return this.count.asObservable();
  }

  public markAsSeen(notification: NotificationVoModel) {

    const notificationUuids = this.extractIdsFromNotifications(notification);

    this.notificationEndpoint.markAsSeen(notificationUuids).subscribe(data => {
      notification.status = 'SEEN';
      this.emitNewCountAfterChanges();
    });
  }

  public remove(notification: NotificationVoModel) {

    const notificationUuids = this.extractIdsFromNotifications(notification);
    this.notificationEndpoint.remove(notificationUuids).subscribe(data => {
      notification.deleted = true;
      this.emitNewCountAfterChanges();
    });
  }

  private reduceNotificationArrayToNestedNotification(notifications: NotificationVoModel[]) {
    return notifications.reduce((accumulator, currentValue, currentIndex, array) => {

      if (Array.isArray(accumulator.uuids)) {
        accumulator.uuids.push(<string>currentValue.uuid);
      } else {
        accumulator.uuids = [];
        accumulator.uuids.push(accumulator.uuid);
        accumulator.uuids.push(currentValue.uuid);
      }

      accumulator.content =  currentValue.groupContent?.replace('{count}', accumulator.uuids.length.toString()) ;

      return accumulator;
    });

  }

  private getHotNotification(): void {
    this.notificationEndpoint.getHotNotification()
      .pipe(map((data) => {

          const newNotifications: NotificationVoModel[] = [];
          if(data){
            data.forEach(notification => {
              const duplicateNotification = this.notifications.find(item => {
                if(item.uuids && item.uuids.length > 0){
                  return item.uuids.indexOf(notification.uuid) != -1;
                } else{
                  return item.uuid === notification.uuid;
                }
              });

              if(!duplicateNotification){
                newNotifications.push(notification);
              }
            });
          }

          if(data && data.length > 0 && newNotifications.length === 0){
            return null;
          }

          const groupByStatus = _.values(_.groupBy(newNotifications, 'status'));
          const allNotification = [];

          groupByStatus.forEach(item => {
            const reduceedNotification = [];
            const groupedByType = _.values(_.groupBy(item, 'actionKey'));

            groupedByType.forEach((element) => {
              if (element.length > 2) {
                reduceedNotification.push(this.reduceNotificationArrayToNestedNotification(element));
              } else {
                reduceedNotification.push(...element);
              }
            });
            allNotification.push(...reduceedNotification);
          });

          return allNotification.sort((a, b) => {
            return Date.parse(a.createdAt) < Date.parse(b.createdAt) ? 1 : -1;
          });

        })
      )
      .subscribe(
        data => {
          if(data){
            if (data.length === 0) {
              this.notifications.splice(0, this.notifications.length); // delete all items
              this.count.next('0');
            } else {
              this.notifications.unshift(...data);
              this.notificationEndpoint.pushToastNotifications(data);
              const newNotification = this.notifications.filter(item => (item.status === 'PENDING' || item.status === 'SENDING' || item.status === 'SENT'));
              this.count.next(newNotification.length.toString());
            }
          }
        });
  }

  private emitNewCountAfterChanges(): void {
    const newNotification = this.notifications.filter(item =>
      (item.status === 'PENDING' || item.status === 'SENDING' || item.status === 'SENT') && (item.deleted !== true));
    this.count.next(newNotification.length.toString());
  }

  private extractIdsFromNotifications(notification: NotificationVoModel): string[] {
    const notifications: NotificationVoModel[] = Array.isArray(notification)
      ? <NotificationVoModel[]>notification :
      <NotificationVoModel[]>[notification];

    const notificationUuids: string[] = [];

    notifications.forEach(item => {
      if (item.uuids) {
        notificationUuids.push(...item.uuids);
      } else {
        notificationUuids.push(item.uuid);
      }
    });

    return notificationUuids;
  }
}
